/**
 * Typed fetch wrapper for the dotlogic agents kernel API.
 *
 * - All paths are relative to `/api` (`api.get("/tenants")` → `GET /api/tenants`).
 * - Success responses are the resource itself (or `undefined` for 204).
 * - Errors are thrown as {@link ApiError} carrying the HTTP status, the server's
 *   machine-readable `code` and an optional per-field validation map.
 * - Every request carries `X-Tenant-Id` from the per-tab tenant store, so two tabs
 *   can work in two organizations at the same time.
 * - A 401 on a non-auth endpoint triggers the session-lost handler (see
 *   {@link setSessionLostHandler}); the app wires that to "clear cache + go to /login".
 */
import { useTenantStore } from "@/stores/tenant"

export const API_BASE = "/api"

export type FieldErrors = Record<string, string>

export interface ApiErrorBody {
  error?: { code?: string; message?: string; fields?: FieldErrors }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: FieldErrors | undefined
  /** Seconds to wait before retrying (from `Retry-After`, mainly for 429). */
  readonly retryAfter: number | undefined

  constructor(status: number, code: string, message: string, fields?: FieldErrors, retryAfter?: number) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
    this.fields = fields && Object.keys(fields).length > 0 ? fields : undefined
    this.retryAfter = retryAfter
  }

  get isUnauthorized() {
    return this.status === 401
  }
  get isForbidden() {
    return this.status === 403
  }
  get isNotFound() {
    return this.status === 404
  }
  get isConflict() {
    return this.status === 409
  }
  get isValidation() {
    return this.status === 422
  }
  get isRateLimited() {
    return this.status === 429
  }
  get isNetwork() {
    return this.status === 0
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface RequestOptions {
  query?: QueryParams
  headers?: Record<string, string>
  signal?: AbortSignal
  /**
   * Tenant to scope the request to. Defaults to the active tenant of this tab.
   * Pass `null` to omit the header (server falls back to the session's tenant).
   */
  tenantId?: number | null
  /** Do not trigger the global session-lost handler on 401 (used by auth calls). */
  skipSessionLost?: boolean
}

type SessionLostHandler = () => void

let sessionLostHandler: SessionLostHandler = () => {
  const here = window.location.pathname + window.location.search
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign(`/login?redirect=${encodeURIComponent(here)}`)
  }
}

/** Replace the default 401 behaviour (a hard redirect) with an app-level handler. */
export function setSessionLostHandler(handler: SessionLostHandler) {
  sessionLostHandler = handler
}

export function buildUrl(path: string, query?: QueryParams): string {
  const p = path.startsWith("/") ? path : `/${path}`
  let url = p.startsWith(`${API_BASE}/`) ? p : `${API_BASE}${p}`
  if (query) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue
      qs.set(k, String(v))
    }
    const s = qs.toString()
    if (s) url += (url.includes("?") ? "&" : "?") + s
  }
  return url
}

/** Headers shared by every API request (JSON + tenant scoping). */
export function baseHeaders(opts: Pick<RequestOptions, "tenantId" | "headers"> = {}): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" }
  const tenantId = opts.tenantId === undefined ? useTenantStore.getState().tenantId : opts.tenantId
  if (tenantId != null) headers["X-Tenant-Id"] = String(tenantId)
  return { ...headers, ...opts.headers }
}

const AUTH_PATH = /^(\/api)?\/auth\//

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined
  try {
    const text = await res.text()
    body = text ? (JSON.parse(text) as ApiErrorBody) : undefined
  } catch {
    body = undefined
  }
  const code = body?.error?.code ?? `http_${res.status}`
  const message = body?.error?.message ?? (res.statusText || `HTTP ${res.status}`)
  const retryHeader = res.headers.get("retry-after")
  const retryAfter = retryHeader && /^\d+$/.test(retryHeader) ? Number(retryHeader) : undefined
  return new ApiError(res.status, code, message, body?.error?.fields, retryAfter)
}

/** Turn a non-OK response into an ApiError and run the session-lost hook when needed. */
export async function toApiError(res: Response, path: string, opts: RequestOptions = {}): Promise<ApiError> {
  const err = await parseError(res)
  if (err.status === 401 && !opts.skipSessionLost && !AUTH_PATH.test(path)) {
    sessionLostHandler()
  }
  return err
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const headers = baseHeaders(opts)
  let payload: BodyInit | undefined
  if (body instanceof FormData) {
    payload = body
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json"
    payload = JSON.stringify(body)
  }

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method,
      headers,
      body: payload,
      credentials: "same-origin",
      signal: opts.signal,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e
    throw new ApiError(0, "network_error", e instanceof Error ? e.message : "Network error")
  }

  if (!res.ok) throw await toApiError(res, path, opts)
  if (res.status === 204 || res.headers.get("content-length") === "0") return undefined as T

  const text = await res.text()
  if (!text) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(res.status, "invalid_json", "The server returned an invalid response.")
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, undefined, opts),
  post: <T = void>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>("POST", path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", path, body, opts),
  delete: <T = void>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, undefined, opts),
}

/** Standard collection envelope returned by list endpoints. */
export interface ListResponse<T> {
  items: T[]
  total?: number
  next_cursor?: string
}
